// =============================================================================
// VandieZ admin-web -- Bank verification queue (/bank-accounts/pending)
// =============================================================================
// The ops work queue created by defect DR-24. Payouts are now gated on a
// bank account being verified, and the only route to "verified" is an
// admin manually attesting they checked a document — so this list is the
// backlog standing between drivers and their money.
//
// Migration 0064 parked every previously stub-verified account into
// `pending`, which is why this queue is non-empty on day one. Oldest
// request first: the endpoint orders by verification_requested_at ASC.
//
// Review opens the shared <BankVerificationModal/> (same component the
// user-detail page uses) so both surfaces record identical attestations.
// =============================================================================

import { AlertCircle, RefreshCw, ShieldCheck } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";

import { bankAccountsAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import {
  BankVerificationModal,
  BankVerificationStatusPill,
} from "@/components/BankVerificationModal";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { TableSkeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type { AdminUserBankAccountRow } from "@/types/admin_user_banks";

const PAGE_SIZE = 50;

function formatDateTime(iso: string | null): string {
  if (iso === null) {
    return "—";
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function BankVerificationQueuePage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [rows, setRows] = useState<AdminUserBankAccountRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState<AdminUserBankAccountRow | null>(
    null,
  );

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await bankAccountsAdminApi.pending(
          accessToken,
          { limit: PAGE_SIZE, offset: 0 },
          signal,
        );
        setRows(res.rows);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        setError(
          e instanceof ApiError ? e.problem.detail : "Unexpected error.",
        );
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => { controller.abort(); };
  }, [load]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Bank verification queue"
        description="Accounts awaiting a manual check, oldest request first. A driver cannot be paid out until someone here vouches for their account."
        actions={
          <Button variant="outline" size="sm" onClick={() => { void load(); }}>
            <RefreshCw className="mr-1 h-4 w-4" /> Refresh
          </Button>
        }
      />

      {error !== null && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Pending accounts ({rows.length})</CardTitle>
          <CardDescription>
            Check the holder name, IFSC and last-4 against a cancelled
            cheque, statement or passbook before approving. Every decision is
            recorded against your name.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && rows.length === 0 ? (
            <TableSkeleton columns={6} />
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              Nothing waiting — every bank account has been reviewed.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-4">Holder name</th>
                    <th className="py-2 pr-4">Bank / IFSC</th>
                    <th className="py-2 pr-4">Account</th>
                    <th className="py-2 pr-4">Requested</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b last:border-b-0">
                      <td className="py-3 pr-4 font-medium">
                        {r.account_holder_name}
                        {r.is_primary && (
                          <span className="ml-2 rounded-full bg-info-subtle px-2 py-0.5 text-xs text-info-subtle-foreground">
                            primary
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <div>{r.bank_name}</div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {r.ifsc_code}
                          {r.branch_name !== null && <> · {r.branch_name}</>}
                        </div>
                      </td>
                      <td className="py-3 pr-4 font-mono">
                        ••••{r.account_number_last_4}
                      </td>
                      <td className="py-3 pr-4 text-xs text-muted-foreground">
                        {formatDateTime(r.verification_requested_at)}
                      </td>
                      <td className="py-3 pr-4">
                        <BankVerificationStatusPill
                          status={r.verification_status}
                        />
                      </td>
                      <td className="py-3 pr-4">
                        <Button
                          size="sm"
                          onClick={() => { setReviewing(r); }}
                        >
                          <ShieldCheck className="mr-1 h-3 w-3" /> Review
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {reviewing !== null && (
        <BankVerificationModal
          userId={reviewing.user_id}
          account={reviewing}
          onClose={() => { setReviewing(null); }}
          onDecided={() => {
            // The row leaves `pending` on any decision, so refetch rather
            // than patching it in place.
            void load();
          }}
        />
      )}
    </div>
  );
}
